(function () {
  "use strict";


  /* WH-172: bounded/retrying fetch with plain-fetch fallback; cart POSTs are
     duplicate-safe via cartMutate. */
  const ehFetch = (url, opts) =>
    window.EH && window.EH.fetchRetry ? window.EH.fetchRetry(url, opts) : fetch(url, opts);
  const ehCartPost = (url, payload) =>
    window.EH && window.EH.cartMutate
      ? window.EH.cartMutate(url, payload)
      : fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

  const CART_SVG = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 001.99 1.61h9.72a2 2 0 001.99-1.72l1.38-9.28H6"/></svg>`;
  const CHECK_SVG = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>`;

  class ShopBySymptoms {
    constructor(el) {
      this.el = el;
      this.tabs = Array.from(el.querySelectorAll(".sbs-tab"));
      this.panels = Array.from(el.querySelectorAll(".sbs-panel"));
      this.loaded = new Set();

      const firstPanel = this.panels[0];
      if (firstPanel && firstPanel.dataset.collection) {
        this.loaded.add(firstPanel.dataset.collection);
      }

      this._bind();
    }

    _bind() {
      this.tabs.forEach((tab) =>
        tab.addEventListener("click", () => this._switchTab(tab)),
      );

      this.el.addEventListener("click", (e) => {
        const btn = e.target.closest(".sbs-card__add");
        if (btn) this._addToCart(btn);
      });

      this.panels.forEach((panel) => {
        const scroll = panel.querySelector("[data-products-scroll]");
        if (scroll) {
          this._dragScroll(scroll);
          this._attachProgress(scroll);
        }
      });
    }

    _attachProgress(scroll) {
      // Insert progress indicator after the scroll container
      const wrapper = document.createElement("div");
      wrapper.className = "sbs-progress";
      wrapper.innerHTML =
        '<div class="sbs-progress__bar"><div class="sbs-progress__thumb"></div></div>';
      scroll.after(wrapper);

      const thumb = wrapper.querySelector(".sbs-progress__thumb");
      const bar = wrapper.querySelector(".sbs-progress__bar");

      const update = () => {
        const maxScroll = scroll.scrollWidth - scroll.clientWidth;
        if (maxScroll <= 0) {
          wrapper.style.display = "none";
          return;
        }
        wrapper.style.display = "";
        const barW = bar.offsetWidth;
        const ratio = scroll.clientWidth / scroll.scrollWidth;
        const thumbW = Math.max(20, Math.round(barW * ratio));
        const thumbL = Math.round(
          (barW - thumbW) * (scroll.scrollLeft / maxScroll),
        );
        thumb.style.width = thumbW + "px";
        thumb.style.left = thumbL + "px";
      };

      scroll.addEventListener("scroll", update, { passive: true });

      // Re-evaluate when images load / content changes
      const ro = new ResizeObserver(update);
      ro.observe(scroll);
      update();
    }

    _switchTab(tab) {
      this.tabs.forEach((t) => {
        t.classList.remove("is-active");
        t.setAttribute("aria-selected", "false");
      });
      tab.classList.add("is-active");
      tab.setAttribute("aria-selected", "true");

      this.panels.forEach((p) => p.classList.remove("is-active"));

      const panel = this.el.querySelector(`#sbs-panel-${tab.dataset.tabId}`);
      if (!panel) return;
      panel.classList.add("is-active");
    }

    async _fetchProducts(panel, handle, tabLabel) {
      const scroll = panel.querySelector("[data-products-scroll]");
      if (!scroll) return;

      scroll.innerHTML = '<div class="sbs-loading">Loading&hellip;</div>';

      try {
        const res = await ehFetch(
          `/collections/${encodeURIComponent(handle)}/products.json?limit=30`,
        );
        if (!res.ok) throw new Error("bad response");
        const { products } = await res.json();

        if (!products || !products.length) {
          scroll.innerHTML =
            '<p class="sbs-empty">No products in this category yet.</p>';
          return;
        }

        scroll.innerHTML = products
          .map((p) => this._cardHTML(p, tabLabel))
          .join("");
        this.loaded.add(handle);
        this._dragScroll(scroll);

        // Remove old progress bar if present, then re-attach
        const oldProgress = scroll.nextElementSibling;
        if (oldProgress && oldProgress.classList.contains("sbs-progress"))
          oldProgress.remove();
        this._attachProgress(scroll);
      } catch (_) {
        scroll.innerHTML = '<p class="sbs-empty">Could not load products.</p>';
      }
    }

    _cardHTML(p, tabLabel) {
      const tags = Array.isArray(p.tags)
        ? p.tags.map((t) => t.toLowerCase())
        : [];
      const isBestseller = tags.includes("bestseller");
      const isOncologist = tags.some((t) => t.includes("oncologist"));

      const variant0 = p.variants && p.variants[0];
      const price = this._money(variant0 ? variant0.price : p.price_min);
      const rawCompare = variant0
        ? variant0.compare_at_price
        : p.compare_at_price_min;
      const compare = rawCompare ? this._money(rawCompare) : null;
      const rawImg =
        (p.featured_image && (p.featured_image.src || p.featured_image)) ||
        (p.images && p.images[0] && (p.images[0].src || p.images[0])) ||
        "";
      const img = rawImg ? this._sizedImg(String(rawImg), 400) : "";
      const variantId = p.variants[0].id;
      const title = this._esc(p.title);
      const curated = isOncologist
        ? "Oncologist Curated"
        : tabLabel || "Clinically Reviewed";

      return `<div class="sbs-card">
  <a href="/products/${p.handle}" class="sbs-card__image-wrap">
    ${isBestseller ? '<span class="sbs-card__badge">Bestseller</span>' : ""}
    ${
      img
        ? `<img src="${img}" alt="${title}" loading="lazy" class="sbs-card__img" width="400" height="400">`
        : '<div class="sbs-card__img-placeholder"></div>'
    }
  </a>
  <div class="sbs-card__body">
    <div class="sbs-card__meta">
      <span class="sbs-card__rating">4.5 ★</span>
      <span class="sbs-card__meta-sep">|</span>
      <span class="sbs-card__curated">${curated}</span>
    </div>
    <a href="/products/${p.handle}" class="sbs-card__title">${title}</a>
    <div class="sbs-card__price-row">
      ${compare ? `<s class="sbs-card__compare">${compare}</s>` : ""}
      <span class="sbs-card__price">${price}</span>
    </div>
    <button class="sbs-card__add" aria-label="Add ${title} to cart" data-variant-id="${variantId}">
      ${CART_SVG}
    </button>
  </div>
</div>`;
    }

    _getDrawerSectionId() {
      const el = document.querySelector("cart-drawer");
      if (!el) return null;
      const section = el.closest(".shopify-section");
      return section ? section.id.replace("shopify-section-", "") : null;
    }

    async _addToCart(btn) {
      const variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      btn.disabled = true;
      btn.innerHTML = CHECK_SVG;

      try {
        const sectionId = this._getDrawerSectionId();
        const body = { items: [{ id: variantId, quantity: 1 }] };
        if (sectionId) body.sections = sectionId;

        const addRes = await ehCartPost("/cart/add.js", body);
        const addResp = await addRes.json();
        await this._refreshCart(addResp.sections);
        /* ── Analytics ── */
        var card = btn.closest('.sbs-card');
        var title = card ? (card.querySelector('.sbs-card__title') || {}).textContent || '' : '';
        var priceEl = card ? card.querySelector('.sbs-card__price') : null;
        var priceText = priceEl ? priceEl.textContent.replace(/[^\d.]/g, '') : '0';
        if (window.fireAddToCartEvent) window.fireAddToCartEvent(String(variantId), title.trim(), parseFloat(priceText) || 0);
      } catch (_) {
        // silently restore
      }

      setTimeout(() => {
        btn.innerHTML = CART_SVG;
        btn.disabled = false;
      }, 1500);
    }

    async _refreshCart(addedSections) {
      try {
        const res = await ehFetch("/cart.js");
        const cart = await res.json();
        if (addedSections) cart.sections = addedSections;
        document.querySelectorAll("[data-cart-count]").forEach((el) => {
          el.textContent = cart.item_count;
        });
        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: { baseEvent: "variant:add", cart },
          }),
        );
      } catch (_) {}
    }

    _dragScroll(el) {
      let active = false,
        startX,
        startLeft;

      el.addEventListener("mousedown", (e) => {
        active = true;
        startX = e.pageX - el.offsetLeft;
        startLeft = el.scrollLeft;
        el.classList.add("is-dragging");
      });

      document.addEventListener("mouseup", () => {
        if (!active) return;
        active = false;
        el.classList.remove("is-dragging");
      });

      el.addEventListener("mousemove", (e) => {
        if (!active) return;
        e.preventDefault();
        el.scrollLeft = startLeft - (e.pageX - el.offsetLeft - startX) * 1.5;
      });

      el.addEventListener("mouseleave", () => {
        active = false;
        el.classList.remove("is-dragging");
      });
    }

    _sizedImg(src, w) {
      if (!src || typeof src !== "string") return src || "";
      const sep = src.includes("?") ? "&" : "?";
      return src + sep + "width=" + w;
    }

    _money(val) {
      const num = typeof val === "number" ? val : Number(val);
      if (!num || isNaN(num)) return "";
      const amount = (num / 100).toFixed(2).replace(/\.00$/, "");
      const fmt =
        (window.themeVariables && window.themeVariables.moneyFormat) ||
        "₹ {{amount}}";
      return fmt
        .replace(/\{\{\s*amount[^}]*\}\}/, amount)
        .replace(/Rs\.?\s*/g, "₹ ");
    }

    _esc(str) {
      return String(str || "").replace(
        /[&<>"']/g,
        (c) =>
          ({
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
          })[c],
      );
    }
  }

  function init() {
    document
      .querySelectorAll("[data-shop-by-symptoms]")
      .forEach((el) => new ShopBySymptoms(el));
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
